import { getStoredAuthSession } from '@/store/auth/authStorage';
import type { AuthStatus, AuthTokens } from '@/store/auth/auth.types';
import { checkNetworkConnectivity } from '@/utils/network/network.utils';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useRef } from 'react';

type UseReconnectSessionOnNetworkBackParams = {
  hasRestoredSession: boolean;
  status: AuthStatus;
  isLoggingIn: boolean;
  setStatus: Dispatch<SetStateAction<AuthStatus>>;
  setIsReconnectingSession: Dispatch<SetStateAction<boolean>>;
  refreshTokens: (refreshToken: string) => Promise<AuthTokens | null>;
};

/**
 * Retry refresh once connectivity comes back after a degraded offline session.
 * This avoids redirecting to login immediately when network returns.
 */
export const useReconnectSessionOnNetworkBack = ({
  hasRestoredSession,
  status,
  isLoggingIn,
  setStatus,
  setIsReconnectingSession,
  refreshTokens,
}: UseReconnectSessionOnNetworkBackParams): void => {
  const hasAttemptedReconnectRefreshRef = useRef(false);

  useEffect(() => {
    if (!hasRestoredSession) {
      hasAttemptedReconnectRefreshRef.current = false;
      setIsReconnectingSession(false);
      return;
    }

    if (status !== 'unauthenticated' || isLoggingIn) {
      hasAttemptedReconnectRefreshRef.current = false;
      setIsReconnectingSession(false);
      return;
    }

    let isCancelled = false;

    const tryReconnectSession = async (): Promise<void> => {
      const storedSession = await getStoredAuthSession();
      if (!storedSession?.refreshToken) {
        hasAttemptedReconnectRefreshRef.current = false;
        return;
      }

      const isOnline = await checkNetworkConnectivity();
      if (!isOnline) {
        hasAttemptedReconnectRefreshRef.current = false;
        return;
      }

      if (hasAttemptedReconnectRefreshRef.current) {
        return;
      }

      hasAttemptedReconnectRefreshRef.current = true;
      setIsReconnectingSession(true);

      try {
        const refreshedTokens = await refreshTokens(storedSession.refreshToken);
        if (!refreshedTokens && !isCancelled) {
          setStatus('unauthenticated');
        }
      } finally {
        if (!isCancelled) {
          setIsReconnectingSession(false);
        }
      }
    };

    void tryReconnectSession();

    const reconnectInterval = setInterval(() => {
      void tryReconnectSession();
    }, 10000);

    return (): void => {
      isCancelled = true;
      clearInterval(reconnectInterval);
    };
  }, [
    hasRestoredSession,
    isLoggingIn,
    refreshTokens,
    setIsReconnectingSession,
    setStatus,
    status,
  ]);
};
